'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { GroqImage } from '@/helpers/Image';
import { PageAboutUsSchema } from '@/sanity/pages/PageAboutUsTemplate';

const ease = [0.25, 0.1, 0.25, 1] as const;

function QuoteMark() {
  return (
    <svg
      viewBox="0 0 60.1783 45.1337"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="lg:h-[5.625rem] w-[6rem] h-[4.5rem] lg:w-[7.5rem] shrink-0"
      aria-hidden="true"
    >
      <path
        d="M25.0317 0V18.5324C25.0317 32.8348 15.6764 42.5285 2.50743 45.1337L0.0125371 39.7402C6.1106 37.4409 10.0297 30.6182 10.0297 25.0743H0V0H25.0317ZM60.1783 0V18.5324C60.1783 32.8348 50.7804 42.531 37.6114 45.1337L35.114 39.7402C41.2146 37.4409 45.1337 30.6182 45.1337 25.0743H35.1466V0H60.1783Z"
        fill="#228E71"
      />
    </svg>
  );
}

export default function FounderQuoteSection({
  data,
}: {
  data: PageAboutUsSchema;
}) {
  if (!data.founderQuoteText) return null;

  return (
    <section className="relative mx-auto lg:w-[229.875rem] w-[98%] overflow-hidden bg-[#04271d] py-[7.25rem] lg:py-[20rem]">
      {/* Tiled texture overlay */}
      {data.founderQuoteBg && (
        <div
          className="pointer-events-none absolute top-0 lg:inset-0 w-[calc(100%+50rem)] h-[calc(100%+50rem)] flex"
          aria-hidden="true"
        >
          <div className="relative h-full w-full opacity-60">
            <GroqImage
              src={data.founderQuoteBg}
              alt=""
              className="!h-full !w-full !object-cover"
            />
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease }}
        className="relative z-10 mx-auto flex w-[min(106.5rem,calc(100%-8rem))] flex-col items-center gap-[2.25rem] lg:gap-[4rem]"
      >
        <QuoteMark />

        <div className="richtext text-center prose-p:text-[3rem] prose-p:lg:text-[4.5rem] prose-p:font-normal prose-p:leading-[1.35] prose-p:tracking-[-0.225rem] prose-p:text-white prose-strong:font-semibold prose-strong:text-white">
          <PortableText
            value={data.founderQuoteText}
            components={richTextComponent}
          />
        </div>

        {/* Vertical divider */}
        <div className="lg:h-[4.875rem] h-[8rem] w-px bg-white/20" />

        {(data.founderQuoteName || data.founderQuoteRole) && (
          <div className="flex flex-col items-center gap-[0.75rem]">
            {data.founderQuoteName && (
              <p className="mb-0 text-[2.5rem] lg:text-[3.5rem] font-medium leading-[1.12] tracking-[-0.14rem] text-white">
                {data.founderQuoteName}
              </p>
            )}
            {data.founderQuoteRole && (
              <p className="mb-0 font-geist-mono text-[1.75rem] font-medium uppercase tracking-[-0.0525rem] text-[#228e71]">
                {data.founderQuoteRole}
              </p>
            )}
          </div>
        )}
      </motion.div>
    </section>
  );
}
